import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Input from '../../components/input/Input';
import CustomerAddressService from './CustomerAddressService';

const EMPTY_FORM = {
  name: '',
  phone: '',
  addressName: '',
  address: '',
  subdistrict: '',
  district: '',
  province: '',
  postcode: '',
  locationLink: '',
  isDefaultAddress: false,
};

// Admin view/manage of a customer's real address book — the CustomerAddress rows
// a parcel actually ships to (P-ADMIN-1). Self-contained: saves via its own
// endpoints, independent of the profile edit form.
const CustomerAddressBook = ({ customerId }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // null | 'new' | <addressId>
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await CustomerAddressService.getAddresses(customerId);
      setAddresses(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'ไม่สามารถโหลดที่อยู่ได้');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const startNew = () => {
    setForm(EMPTY_FORM);
    setMode('new');
  };

  const startEdit = (addr) => {
    setForm({
      name: addr.name || '',
      phone: addr.phone || '',
      addressName: addr.addressName || '',
      address: addr.address || '',
      subdistrict: addr.subdistrict || '',
      district: addr.district || '',
      province: addr.province || '',
      postcode: addr.postcode || '',
      locationLink: addr.locationLink || '',
      isDefaultAddress: !!addr.isDefaultAddress,
    });
    setMode(addr.id);
  };

  const cancel = () => {
    setMode(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'new') {
        await CustomerAddressService.createAddress(customerId, form);
        toast.success('เพิ่มที่อยู่แล้ว');
      } else {
        await CustomerAddressService.updateAddress(mode, form);
        toast.success('แก้ไขที่อยู่แล้ว');
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกที่อยู่ไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (addr) => {
    if (!window.confirm(`ลบที่อยู่ "${addr.addressName}" ?`)) return;
    try {
      await CustomerAddressService.deleteAddress(addr.id);
      toast.success('ลบที่อยู่แล้ว');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'ลบที่อยู่ไม่สำเร็จ');
    }
  };

  const makeDefault = async (addr) => {
    try {
      await CustomerAddressService.setDefaultAddress(addr.id);
      toast.success('ตั้งเป็นที่อยู่หลักแล้ว');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'ตั้งค่าที่อยู่หลักไม่สำเร็จ');
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-end">
        {mode === null && (
          <button
            type="button"
            onClick={startNew}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
          >
            + เพิ่มที่อยู่
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-gray-500">กำลังโหลด...</div>}

      {!loading && addresses.length === 0 && mode === null && (
        <div className="text-sm text-gray-500">ยังไม่มีที่อยู่</div>
      )}

      {mode === null && (
        <div className="space-y-2">
          {addresses.map((addr) => (
            <div key={addr.id} className="rounded border border-gray-200 p-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{addr.addressName}</span>
                {addr.isDefaultAddress && (
                  <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">ค่าเริ่มต้น</span>
                )}
              </div>
              <div className="text-sm text-gray-700">
                {addr.name} · {addr.phone}
              </div>
              <div className="text-sm text-gray-600">
                {[addr.address, addr.subdistrict, addr.district, addr.province, addr.postcode]
                  .filter(Boolean)
                  .join(' ')}
              </div>
              {addr.locationLink && (
                <a
                  href={addr.locationLink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 underline"
                >
                  แผนที่
                </a>
              )}
              <div className="mt-1 flex gap-2 text-sm">
                {!addr.isDefaultAddress && (
                  <button type="button" onClick={() => makeDefault(addr)} className="text-green-700 hover:underline">
                    ตั้งเป็นหลัก
                  </button>
                )}
                <button type="button" onClick={() => startEdit(addr)} className="text-blue-600 hover:underline">
                  แก้ไข
                </button>
                <button type="button" onClick={() => remove(addr)} className="text-red-600 hover:underline">
                  ลบ
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode !== null && (
        <div className="grid grid-cols-2 gap-2">
          <Input title="ชื่อผู้รับ (Name)" name="name" value={form.name} onChange={(v) => setField('name', v)} />
          <Input title="เบอร์โทร (Phone)" name="phone" value={form.phone} onChange={(v) => setField('phone', v)} />
          <Input title="ชื่อที่อยู่ (Label)" name="addressName" value={form.addressName} onChange={(v) => setField('addressName', v)} />
          <Input title="รหัสไปรษณีย์ (Postcode)" name="postcode" value={form.postcode} onChange={(v) => setField('postcode', v)} />
          <div className="col-span-2">
            <Input title="ที่อยู่ (Address)" name="address" value={form.address} onChange={(v) => setField('address', v)} columnSpan={2} />
          </div>
          <Input title="ตำบล (Subdistrict)" name="subdistrict" value={form.subdistrict} onChange={(v) => setField('subdistrict', v)} />
          <Input title="อำเภอ (District)" name="district" value={form.district} onChange={(v) => setField('district', v)} />
          <Input title="จังหวัด (Province)" name="province" value={form.province} onChange={(v) => setField('province', v)} />
          <div className="col-span-2">
            <Input title="ลิงก์แผนที่ (Location link)" name="locationLink" value={form.locationLink} onChange={(v) => setField('locationLink', v)} columnSpan={2} />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isDefaultAddress}
              onChange={(e) => setField('isDefaultAddress', e.target.checked)}
            />
            ตั้งเป็นที่อยู่หลัก
          </label>
          <div className="col-span-2 flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="rounded bg-blue-600 px-4 py-1 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
            <button type="button" onClick={cancel} className="rounded bg-gray-200 px-4 py-1 hover:bg-gray-300">
              ยกเลิก
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAddressBook;
